import { useEffect, useState } from "react";
import { useStore } from "../lib/store";
import { useI18n } from "../lib/i18n";
import { Zap, Copy } from "lucide-react";
import { toast } from "sonner";

const parts = (ms) => ({
  d: Math.floor(ms / 86400000),
  h: Math.floor((ms % 86400000) / 3600000),
  m: Math.floor((ms % 3600000) / 60000),
  s: Math.floor((ms % 60000) / 1000),
});

const Unit = ({ value, label }) => (
  <div className="text-center">
    <div className="num text-2xl sm:text-3xl font-bold tabular-nums bg-white/15 rounded-xl px-3 py-1.5">
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[10px] uppercase tracking-wider mt-1 opacity-80">{label}</div>
  </div>
);

export const FlashSale = () => {
  const { t, lang } = useI18n();
  const { settings } = useStore();
  const [left, setLeft] = useState(0);

  const endsAt = settings?.flash_ends_at;
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => setLeft(new Date(endsAt).getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  if (!settings?.flash_enabled || !endsAt || left <= 0) return null;
  const { d, h, m, s } = parts(left);

  return (
    <div
      data-testid="flash-sale-banner"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-rose-700 text-white p-5 sm:p-6"
    >
      <span className="pointer-events-none absolute -right-8 -top-10 h-40 w-40 rounded-full bg-white/10" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
        <div className="min-w-0 flex-1">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white/20 rounded-full px-3 py-1">
            <Zap size={13} /> {t("flash_label")}
          </p>
          <p className="font-display text-2xl sm:text-3xl font-semibold mt-2 leading-tight">
            {(lang === "ja" && settings.flash_title_ja) || settings.flash_title}
          </p>
          <p className="text-sm opacity-90 mt-1">
            {(lang === "ja" && settings.flash_subtitle_ja) || settings.flash_subtitle}
          </p>
          {settings.flash_code && (
            <button
              data-testid="flash-copy-code"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(settings.flash_code);
                } catch {
                  const el = document.createElement("textarea");
                  el.value = settings.flash_code;
                  document.body.appendChild(el);
                  el.select();
                  document.execCommand("copy");
                  el.remove();
                }
                toast.success(`${t("code_copied")}: ${settings.flash_code}`);
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-gray-900 px-4 py-1.5 text-sm font-semibold hover:scale-105 transition-transform duration-200"
            >
              {settings.flash_code}
              {settings.flash_pct ? ` · -${settings.flash_pct}%` : ""}
              <Copy size={13} />
            </button>
          )}
        </div>
        <div className="flex gap-2 sm:gap-3" data-testid="flash-countdown">
          {d > 0 && <Unit value={d} label={t("days")} />}
          <Unit value={h} label={t("hours")} />
          <Unit value={m} label={t("minutes")} />
          <Unit value={s} label={t("seconds")} />
        </div>
      </div>
    </div>
  );
};
